// First-play controls hint (ui-qol §9.1; owner exception (c)): a ball.io extension in the style
// of agar's in-game bars — a black 40 % box bottom-centre with three rows,
//   [mouse] Move the mouse to steer · [Space] Split in two · [W] Eject a bit of mass
// (touch: Drag to steer · ✂ Tap to split · ● Tap to eject), plus, in money rooms, a
// "[Q] Hold to cash out your value" row (its own "seen" flag, so a player who knows the controls
// still gets told about cash-out once).
//
// Rules:
// - Shown 600 ms after a life's first own cell is drawn, when its storage flag is not '1'
//   (`ballio.onboarding.v1`, `ballio.onboarding.money.v1`). Storage unavailable → once per page.
// - Each row gets a ✓ and drops to opacity 0.5 when done: move = pointer travelled ≥ 150 CSS px
//   since the hint appeared; split / eject / cash-out = that intent was sent.
// - Exit (400 ms fade): 1.5 s after every row is done, 15 s after appearing, on ×, or on
//   `dismiss()` (death, Esc, leaving play). '1' is stored on completion, on ×, or once it has
//   been visible for 5 s.
// - Help ▸ "Show the controls hint again" calls `reset()`.
//
// `ControlsHintLogic` is DOM-free (timers and storage injected; unit-tested). `createControlsHint`
// wraps it in the DOM and implements app.ts `OnboardingHint`.

import type { OnboardingHint } from '../../app.js';
import {
  defaultStorage,
  isOnboardingSeen,
  markOnboardingSeen,
  type OnboardingKind,
  resetOnboarding,
  type StorageLike,
} from '../../settings/store.js';

/** The hint's own kinds (the arena tip is the arena client's, arena/client.ts). */
type HintKind = Exclude<OnboardingKind, 'arena'>;
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';
import { mouseIcon } from '../icons.js';

/** The `cap` of the desktop move row: drawn as a mouse pictogram. */
export const MOUSE_CAP = 'mouse';

export type HintRowId = 'move' | 'split' | 'eject' | 'cashOut';

export interface HintRow {
  readonly id: HintRowId;
  readonly text: string;
  /** Key cap or glyph shown before the text ("Space", "W", "✂", …). */
  readonly cap: string;
  /** The cap is a pictogram (mouse, finger) rather than a key. */
  readonly glyph: boolean;
  readonly done: boolean;
}

export type HintPhase = 'hidden' | 'shown' | 'leaving';

export interface HintView {
  readonly phase: HintPhase;
  readonly rows: readonly HintRow[];
  readonly touch: boolean;
}

export const HINT_TIMING = {
  /** Appears this long after the first own cell is drawn. */
  SHOW_DELAY_MS: 600,
  /** Leaves this long after every row is done. */
  DONE_EXIT_MS: 1500,
  /** Leaves this long after appearing, done or not. */
  MAX_VISIBLE_MS: 15_000,
  /** Counted as seen once visible this long. */
  SEEN_AFTER_MS: 5000,
  /** Fade-out length. */
  FADE_OUT_MS: 400,
  /** Pointer travel that completes the move row (CSS px). */
  MOVE_PX: 150,
} as const;

export interface HintTimers {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const GLOBAL_TIMERS: HintTimers = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

export interface FirstCellInfo {
  readonly touch: boolean;
  readonly money: boolean;
  readonly cashOutKey: string;
}

/** The rows for a life (DOM-free): controls rows unless seen, the money row unless seen. */
export function hintRows(
  info: FirstCellInfo,
  want: { readonly controls: boolean; readonly money: boolean },
): HintRow[] {
  const rows: HintRow[] = [];
  if (want.controls) {
    if (info.touch) {
      rows.push(
        { id: 'move', text: t('onboard.touch.move'), cap: '☝', glyph: true, done: false },
        { id: 'split', text: t('onboard.touch.split'), cap: '✂', glyph: true, done: false },
        { id: 'eject', text: t('onboard.touch.eject'), cap: '●', glyph: true, done: false },
      );
    } else {
      rows.push(
        { id: 'move', text: t('onboard.move'), cap: MOUSE_CAP, glyph: true, done: false },
        {
          id: 'split',
          text: t('onboard.split'),
          cap: t('menu.key.space'),
          glyph: false,
          done: false,
        },
        { id: 'eject', text: t('onboard.eject'), cap: t('menu.key.w'), glyph: false, done: false },
      );
    }
  }
  if (want.money && info.money) {
    rows.push(
      info.touch
        ? { id: 'cashOut', text: t('onboard.touch.cashOut'), cap: '$', glyph: true, done: false }
        : {
            id: 'cashOut',
            text: t('onboard.cashOut'),
            cap: (info.cashOutKey || 'Q').toUpperCase(),
            glyph: false,
            done: false,
          },
    );
  }
  return rows;
}

/** Which storage flag a row belongs to. */
function rowKind(id: HintRowId): HintKind {
  return id === 'cashOut' ? 'money' : 'controls';
}

export class ControlsHintLogic {
  private readonly storage: StorageLike | null;
  private readonly timers: HintTimers;
  private readonly onView: (view: HintView) => void;
  private phase: HintPhase = 'hidden';
  private rows: HintRow[] = [];
  private touch = false;
  private pending: unknown = null;
  private readonly live: unknown[] = [];
  private moved = 0;
  private visibleSince = false;
  /** Shown this page load (storage-less fallback: once per page). */
  private readonly shownThisPage = { controls: false, money: false };

  constructor(
    onView: (view: HintView) => void,
    storage: StorageLike | null = defaultStorage(),
    timers: HintTimers = GLOBAL_TIMERS,
  ) {
    this.onView = onView;
    this.storage = storage;
    this.timers = timers;
  }

  get view(): HintView {
    return { phase: this.phase, rows: this.rows, touch: this.touch };
  }

  /** Whether a kind still needs showing (not stored as seen, not shown this page). */
  wants(kind: HintKind): boolean {
    return !this.shownThisPage[kind] && !isOnboardingSeen(kind, this.storage);
  }

  onFirstOwnCell(info: FirstCellInfo): void {
    if (this.phase !== 'hidden' || this.pending !== null) return;
    const rows = hintRows(info, { controls: this.wants('controls'), money: this.wants('money') });
    if (rows.length === 0) return;
    this.pending = this.timers.setTimeout(() => {
      this.pending = null;
      this.open(rows, info.touch);
    }, HINT_TIMING.SHOW_DELAY_MS);
  }

  onAction(action: 'split' | 'eject' | 'cashOut'): void {
    this.complete(action);
  }

  /** Pointer travel in CSS px (the DOM part feeds pointermove deltas while shown). */
  onPointerTravel(px: number): void {
    if (this.phase !== 'shown' || !(px > 0)) return;
    this.moved += px;
    if (this.moved >= HINT_TIMING.MOVE_PX) this.complete('move');
  }

  /** The × button. */
  close(): void {
    if (this.phase !== 'shown') return;
    this.markSeen();
    this.leave();
  }

  /** Death, Esc or leaving play: fade out now (a pending show is cancelled). */
  dismiss(): void {
    this.cancelPending();
    if (this.phase === 'shown') this.leave();
  }

  /** Help ▸ "Show the controls hint again": clears the flags; the next life shows it again. */
  reset(): void {
    resetOnboarding(this.storage);
    this.shownThisPage.controls = false;
    this.shownThisPage.money = false;
  }

  private open(rows: HintRow[], touch: boolean): void {
    this.rows = rows;
    this.touch = touch;
    this.moved = 0;
    this.visibleSince = true;
    this.phase = 'shown';
    for (const r of rows) this.shownThisPage[rowKind(r.id)] = true;
    this.later(() => this.leave(), HINT_TIMING.MAX_VISIBLE_MS);
    this.later(() => {
      if (this.phase === 'shown') this.markSeen();
    }, HINT_TIMING.SEEN_AFTER_MS);
    this.emit();
  }

  private complete(id: HintRowId): void {
    if (this.phase !== 'shown') return;
    const i = this.rows.findIndex((r) => r.id === id && !r.done);
    if (i < 0) return;
    this.rows = this.rows.map((r, j) => (j === i ? { ...r, done: true } : r));
    this.emit();
    if (this.rows.every((r) => r.done)) {
      this.markSeen();
      this.later(() => this.leave(), HINT_TIMING.DONE_EXIT_MS);
    }
  }

  private markSeen(): void {
    const kinds = new Set(this.rows.map((r) => rowKind(r.id)));
    for (const k of kinds) markOnboardingSeen(k, this.storage);
  }

  private leave(): void {
    if (this.phase !== 'shown') return;
    this.clearLive();
    this.phase = 'leaving';
    this.visibleSince = false;
    this.emit();
    this.later(() => {
      this.phase = 'hidden';
      this.rows = [];
      this.emit();
    }, HINT_TIMING.FADE_OUT_MS);
  }

  private later(fn: () => void, ms: number): void {
    const handle = this.timers.setTimeout(() => {
      const i = this.live.indexOf(handle);
      if (i >= 0) this.live.splice(i, 1);
      fn();
    }, ms);
    this.live.push(handle);
  }

  private clearLive(): void {
    for (const handle of this.live.splice(0)) this.timers.clearTimeout(handle);
  }

  private cancelPending(): void {
    if (this.pending === null) return;
    this.timers.clearTimeout(this.pending);
    this.pending = null;
  }

  private emit(): void {
    this.onView(this.view);
  }

  /** True while the box is on screen (tests). */
  get visible(): boolean {
    return this.visibleSince;
  }
}

export interface ControlsHint extends OnboardingHint {
  readonly el: HTMLElement;
  reset(): void;
  readonly logic: ControlsHintLogic;
}

/** The DOM hint (mount `el` in #hud). */
export function createControlsHint(win: Window = window): ControlsHint {
  const list = h('ul', { class: 'hint-rows' });
  const closeBtn = h(
    'button',
    {
      type: 'button',
      class: 'hint-close',
      'aria-label': t('onboard.close'),
      onClick: () => logic.close(),
    },
    '×',
  );
  const el = h(
    'aside',
    { class: 'controls-hint', role: 'status', 'aria-live': 'polite', hidden: true },
    closeBtn,
    list,
  );

  let lastX = Number.NaN;
  let lastY = Number.NaN;
  let tracking = false;
  const onMove = (ev: PointerEvent): void => {
    if (Number.isFinite(lastX))
      logic.onPointerTravel(Math.hypot(ev.clientX - lastX, ev.clientY - lastY));
    lastX = ev.clientX;
    lastY = ev.clientY;
  };
  const track = (on: boolean): void => {
    if (on === tracking) return;
    tracking = on;
    lastX = Number.NaN;
    lastY = Number.NaN;
    if (on) win.addEventListener('pointermove', onMove, { passive: true });
    else win.removeEventListener('pointermove', onMove);
  };

  const render = (view: HintView): void => {
    track(view.phase === 'shown');
    el.classList.toggle('is-touch', view.touch);
    el.classList.toggle('is-leaving', view.phase === 'leaving');
    if (view.phase === 'hidden') {
      show(el, false);
      list.replaceChildren();
      return;
    }
    list.replaceChildren(
      ...view.rows.map((r) =>
        h(
          'li',
          { class: `hint-row hint-${r.id}${r.done ? ' is-done' : ''}` },
          h(
            'span',
            { class: 'hint-lead', 'aria-hidden': 'true' },
            h(
              'span',
              { class: r.glyph ? 'hint-glyph' : 'hint-cap' },
              r.cap === MOUSE_CAP ? mouseIcon() : r.cap,
            ),
          ),
          h('span', { class: 'hint-text' }, r.text),
          h('span', { class: 'hint-check', 'aria-hidden': 'true' }, r.done ? '✓' : ''),
        ),
      ),
    );
    show(el, true);
  };
  const logic = new ControlsHintLogic(render);

  return {
    el,
    logic,
    onFirstOwnCell: (info) => logic.onFirstOwnCell(info),
    onAction: (action) => logic.onAction(action),
    dismiss: () => logic.dismiss(),
    reset: () => logic.reset(),
  };
}
